import { CalendarClock, MapPinPlus } from "lucide-react";

import { RowAction, RowActionSeparator, RowActionsMenu } from "@/components/row-actions-menu";
import {
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { useCanEdit } from "@/lib/capabilities";

export type FieldPilotProgressMode = "measurement_count" | "measurement_value";

export function normalizeFieldPilotProgressMode(progressMode: string): FieldPilotProgressMode {
  return progressMode === "measurement_value" ? "measurement_value" : "measurement_count";
}

export function ProgressRowActionsMenu({
  tagLabel,
  convertAction,
  progressMode,
  onEditSchedule,
  onConvert,
  onProgressModeChange,
}: {
  tagLabel: string;
  convertAction: { label: string; disabled: boolean; muted?: boolean };
  progressMode: string;
  onEditSchedule: () => void;
  onConvert: () => void;
  onProgressModeChange: (progressMode: FieldPilotProgressMode) => void;
}) {
  const progressModeValue = normalizeFieldPilotProgressMode(progressMode);
  // Every action here writes to the job, so reviewers get no menu.
  const canEdit = useCanEdit();
  if (!canEdit) return null;

  return (
    <RowActionsMenu
      label={`Actions for ${tagLabel}`}
      size="compact"
      align="start"
      contentClassName="z-[160]"
    >
      <RowAction icon={CalendarClock} onSelect={onEditSchedule}>
        Edit schedule
      </RowAction>
      <RowAction
        icon={MapPinPlus}
        className={cn(convertAction.muted && "text-muted-foreground")}
        disabled={convertAction.disabled}
        onSelect={onConvert}
      >
        {convertAction.label}
      </RowAction>
      <RowActionSeparator />
      <DropdownMenuLabel className="text-xs text-muted-foreground">Measurement Mode</DropdownMenuLabel>
      <DropdownMenuRadioGroup
        value={progressModeValue}
        aria-label="Measurement Mode"
        onValueChange={(value) => {
          onProgressModeChange(value as FieldPilotProgressMode);
        }}
      >
        <DropdownMenuRadioItem value="measurement_count">Count</DropdownMenuRadioItem>
        <DropdownMenuRadioItem value="measurement_value">Value</DropdownMenuRadioItem>
      </DropdownMenuRadioGroup>
    </RowActionsMenu>
  );
}
